import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { History } from '@mui/icons-material';
import { fmt, getDisplayText, getDisplayTone, tdSx, thSx } from './shared';
import { colors, status } from '../../theme';

export default function SignalHistorySection({ t, signalHistory, openTickerDrawer }) {
  if (!signalHistory || !signalHistory.signals?.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 2.5, borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ fontSize: 18, color: colors.gray[500] }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: colors.gray[800] }}>
              {t('vpa.history.title', 'Lịch sử khuyến nghị (30 ngày)')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: colors.gray[400], mt: 0.3 }}>
            {t('vpa.history.subtitle', 'Theo dõi các mã từng được khuyến nghị (READY & WATCH_CLOSE)')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.8, alignItems: 'center' }}>
          {[
            { label: t('vpa.history.winRate', 'Win rate'), value: `${signalHistory.win_rate}%`, color: signalHistory.win_rate >= 50 ? colors.success.main : colors.error.main },
            { label: t('vpa.history.avgPnl', 'PnL TB'), value: `${signalHistory.avg_pnl >= 0 ? '+' : ''}${signalHistory.avg_pnl}%`, color: signalHistory.avg_pnl >= 0 ? colors.success.main : colors.error.main },
            { label: t('vpa.history.wins', 'Thắng'), value: signalHistory.wins, color: colors.success.main },
            { label: t('vpa.history.losses', 'Thua'), value: signalHistory.losses, color: colors.error.main },
            { label: t('vpa.history.open', 'Đang mở'), value: signalHistory.open || 0, color: colors.info.main },
          ].map((stat, index) => (
            <Box key={index} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400], textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' }}>{stat.label}</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: stat.color, fontFamily: 'monospace' }}>{stat.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: t('vpa.history.headers.date', 'Ngày') },
                { label: t('vpa.history.headers.ticker', 'Mã') },
                { label: t('vpa.history.headers.action', 'Action') },
                { label: t('vpa.history.headers.setup', 'Setup') },
                { label: t('vpa.history.headers.signalPrice', 'Giá KN'), align: 'right' },
                { label: t('vpa.history.headers.stop', 'Stop'), align: 'right' },
                { label: t('vpa.history.headers.target', 'Target'), align: 'right' },
                { label: t('vpa.history.headers.exitPrice', 'Giá thoát'), align: 'right' },
                { label: t('vpa.history.headers.pnl', 'PnL'), align: 'right' },
                { label: t('vpa.history.headers.daysHeld', 'Ngày giữ'), align: 'center' },
                { label: t('vpa.history.headers.outcome', 'Kết quả'), align: 'center' },
              ].map((header) => (
                <TableCell key={header.label} sx={{ ...thSx, textAlign: header.align || 'left' }}>{header.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {signalHistory.signals.map((signal, idx) => {
              const historyDisplay = signal.display?.signal_history || {};
              const actionDisplay = historyDisplay.action || null;
              const setupDisplay = historyDisplay.setup || null;
              const pnlDisplay = historyDisplay.pnl || null;
              const outcomeDisplay = historyDisplay.outcome || null;
              const actionTone = getDisplayTone(actionDisplay?.tone);
              const outcomeTone = getDisplayTone(outcomeDisplay?.tone);
              return (
                <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: colors.gray[50] } }}>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[500] }}>{signal.date}</Typography></TableCell>
                  <TableCell sx={tdSx}>
                    <Typography
                      onClick={() => openTickerDrawer(signal)}
                      sx={{ fontWeight: 800, fontSize: '0.78rem', color: colors.gray[900], cursor: 'pointer', '&:hover': { color: status.purple.text } }}
                    >{signal.ticker}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: actionTone.color, bgcolor: actionTone.bg, px: 0.6, py: 0.1, borderRadius: 0.6, display: 'inline-block' }}>
                      {getDisplayText(actionDisplay, 'value', t, signal.action_label || '—')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.7rem', color: getDisplayTone(setupDisplay?.tone).color }}>{getDisplayText(setupDisplay, 'value', t)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{fmt(signal.signal_price)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.error.main }}>{signal.stop_loss ? fmt(signal.stop_loss) : '—'}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.success.main }}>{signal.target ? fmt(signal.target) : '—'}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(signal.exit_price)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700, color: getDisplayTone(pnlDisplay?.tone).color }}>
                      {getDisplayText(pnlDisplay, 'value', t)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: colors.gray[500] }}>{signal.days_held}d</Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: outcomeTone.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: outcomeTone.color, whiteSpace: 'nowrap' }}>{getDisplayText(outcomeDisplay, 'value', t, signal.outcome || '—')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
